import {defineStore} from 'pinia';
import {ref, computed} from 'vue';
import {useApiClient} from "@/hooks/api-hook.ts";
import {Client, ClientCreateRequest, ClientUpdateRequest} from "@/api/client/models/index.ts";

export const useCharterClientsStore = defineStore('charter-clients', () => {
  const api = useApiClient();

  // State
  const clients = ref<Client[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const clientsCount = computed(() => clients.value.length);
  const hasClients = computed(() => clients.value.length > 0);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => error.value !== null);

  // Actions
  async function fetchClients(isActive: boolean = true) {
    loading.value = true;
    error.value = null;

    try {
      clients.value = await api.client.get({queryParameters: {isActive: isActive}})
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch clients';
      console.error('Error fetching clients:', err);
    } finally {
      loading.value = false;
    }
  }

  function clearClients() {
    clients.value = [];
    error.value = null;
  }

  function clearError() {
    error.value = null;
  }

  async function addClient(clientCreateRequest: ClientCreateRequest) {
    loading.value = true;
    error.value = null;

    try {
      const newUser = await api.client.post(clientCreateRequest)
      // Refresh the clients list after adding a new client
      await fetchClients();
      return newUser;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add client';
      console.error('Error adding client:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateClient(clientUpdateRequest: ClientUpdateRequest) {
    loading.value = true;
    error.value = null;

    try {
      await api.client.put(clientUpdateRequest)
      // Refresh the clients list after updating a client
      await fetchClients();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update client';
      console.error('Error updating client:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function toggleClientActive(id: number, isActive: boolean) {
    loading.value = true;
    error.value = null;

    try {
      await api.client.byId(id).byIsActive(isActive).post()
      // Refresh the clients list after toggling client active status
      await fetchClients();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to toggle client active status';
      console.error('Error toggling client active status:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    clients,
    loading,
    error,

    // Getters
    clientsCount,
    hasClients,
    isLoading,
    hasError,

    // Actions
    fetchClients,
    clearClients,
    clearError,
    addClient,
    updateClient,
    toggleClientActive
  };
});
